import { DICTATION_MIN_MACOS_VERSION } from '@electron/shared/dictation.types';

import { Icons } from '@/assets/icons/icons';
import { FEATURES, type hasFeature } from '@/features/feature-flags';
import { AppCapability, isMac, isMacOSAtLeast } from '@/utils/capabilities';

import type { SettingsMenuIcon } from './settings-menu';
export interface SettingsMenuContext {
  isCapable(cap: AppCapability): boolean;
  hasFeature: typeof hasFeature;
  hasMcpServerAccess: boolean;
  isWindows(): boolean;
  isInternalUser: boolean;
  hasInternalAccess: boolean;
  hummingbirdEnabled: boolean;
}

export const SETTINGS_MENU_GROUPS = [
  { id: 'app', title: 'App' },
  { id: 'assistant', title: 'Assistant' },
  { id: 'proactivity', title: 'Proactivity' },
  { id: 'connections', title: 'Connections' },
  { id: 'privacy', title: 'Privacy' },
  { id: 'account', title: 'Account' },
  { id: 'internal', title: 'Internal' },
] as const;

export type SettingsMenuGroup = (typeof SETTINGS_MENU_GROUPS)[number]['id'];

export interface SettingsMenuItemConfig {
  title: string;
  hash: string;
  group: SettingsMenuGroup;
  enabled?: (ctx: SettingsMenuContext) => boolean;
  icon: SettingsMenuIcon;
}

export const settingsMenuConfig: SettingsMenuItemConfig[] = [
  {
    title: 'Appearance',
    hash: 'settings/Appearance',
    group: 'app',
    icon: Icons.appearance,
  },
  {
    title: 'General',
    hash: 'settings/General',
    group: 'app',
    icon: Icons.settings,
  },
  {
    title: 'Keyboard Shortcuts',
    hash: 'settings/KeyboardShortcuts',
    group: 'app',
    icon: Icons.mac,
  },
  {
    title: 'System',
    hash: 'settings/System',
    group: 'app',
    icon: Icons.system,
    enabled: ({ isCapable }) => isCapable(AppCapability.DesktopExclusive),
  },
  {
    title: 'Archived Chats',
    hash: 'settings/ArchivedChats',
    group: 'assistant',
    icon: Icons.archive,
  },
  {
    title: 'Chat',
    hash: 'settings/Chat',
    group: 'assistant',
    icon: Icons.chat,
  },
  {
    title: 'Dictation',
    hash: 'settings/Dictation',
    group: 'assistant',
    icon: Icons.recordAudio,
    // macOS only: on-device transcription and the paste helper are both native to it.
    enabled: ({ hasFeature }) =>
      isMac() &&
      isMacOSAtLeast(
        DICTATION_MIN_MACOS_VERSION.major,
        DICTATION_MIN_MACOS_VERSION.minor
      ) &&
      hasFeature(FEATURES.DICTATION),
  },
  {
    title: 'Hover',
    hash: 'settings/Hummingbird',
    group: 'assistant',
    icon: Icons.pictureInPicture,
    enabled: () => isMac(),
  },
  {
    title: 'Meetings',
    hash: 'settings/Meetings',
    group: 'assistant',
    icon: Icons.meetings,
  },
  {
    title: 'Voice',
    hash: 'settings/VoiceMode',
    group: 'assistant',
    // Ignore the device setting here so eligible users can reopen this page and turn speech back on
    // A direct link can still open this page, but Voice Mode and Read Aloud recheck access before use
    enabled: ({ hasFeature, isInternalUser }) =>
      isInternalUser || hasFeature(FEATURES.VOICE_MODE),
    icon: Icons.dictation,
  },
  {
    title: 'Auto-review',
    hash: 'settings/AutoReview',
    group: 'proactivity',
    enabled: ({ hasFeature }) => hasFeature(FEATURES.AUTO_REVIEW),
    icon: Icons.shieldCheck,
  },
  {
    title: 'Journal',
    hash: 'settings/Journal',
    group: 'proactivity',
    enabled: ({ hasFeature }) => hasFeature(FEATURES.JOURNALS),
    icon: Icons.journal,
  },
  {
    title: 'Proactivity',
    hash: 'settings/Proactivity',
    group: 'proactivity',
    enabled: ({ hasFeature, isInternalUser }) =>
      isInternalUser || hasFeature(FEATURES.PROACTIVE_AGENT),
    icon: Icons.sparkles,
  },
  {
    title: 'Channels',
    hash: 'settings/Channels',
    group: 'connections',
    icon: Icons.chat,
  },
  {
    title: 'Import',
    hash: 'settings/Import',
    group: 'connections',
    icon: Icons.upload,
  },
  {
    title: 'MCP Clients',
    hash: 'settings/McpClients',
    group: 'connections',
    enabled: ({ hasMcpServerAccess }) => hasMcpServerAccess,
    icon: Icons.bot,
  },
  {
    title: 'Webhooks',
    hash: 'settings/Webhooks',
    group: 'connections',
    enabled: ({ hasFeature }) => hasFeature(FEATURES.ROUTINE_WEBHOOK_TRIGGERS),
    icon: Icons.plugZap,
  },
  {
    title: 'Data Controls',
    hash: 'settings/DataControls',
    group: 'privacy',
    icon: Icons.dataControls,
  },
  {
    title: 'Privacy Controls',
    hash: 'settings/PrivacyControls',
    group: 'privacy',
    enabled: ({ isCapable }) => isCapable(AppCapability.ContextCapture),
    icon: Icons.privacyControls,
  },
  {
    title: 'Subscription',
    hash: 'settings/Subscription',
    group: 'account',
    icon: Icons.subscription,
  },
  {
    title: 'Support',
    hash: 'settings/Support',
    group: 'account',
    icon: Icons.feedback,
  },
  {
    title: 'Team',
    hash: 'settings/Team',
    group: 'account',
    icon: Icons.attendees,
  },
  {
    title: 'Usage',
    hash: 'settings/Usage',
    group: 'account',
    icon: Icons.insights,
  },
  {
    title: 'Backend',
    hash: 'settings/Backend',
    group: 'internal',
    enabled: ({ isInternalUser }) => isInternalUser,
    icon: Icons.server,
  },
  {
    title: 'Browser',
    hash: 'settings/Browser',
    group: 'internal',
    enabled: ({ isInternalUser }) => isInternalUser,
    icon: Icons.globe,
  },
  {
    title: 'Proactive agent',
    hash: 'settings/ProactiveAgent',
    group: 'internal',
    enabled: ({ isInternalUser }) => isInternalUser,
    icon: Icons.bot,
  },
];
